<script setup>
/**
 * SharePopup — the Share experience as a modal. Leads with a prominent
 * "Share with your circle" hero (the public on/off state is the most
 * important thing), then Copy Link and an invite box. Invites go by email
 * only (texting plan, 2026-10-31: we never text a number someone else typed);
 * a typed number is turned away in place, pointing to the link. A friend picker appears only when
 * the seeker already has friends (adding friends is a later step), per the
 * social-sharing plan.
 *
 * Referral attribution is NOT here — it happens when an invited/link viewer
 * logs in at the reading's summary gate (capture-referral).
 */
import { ref, computed, onMounted } from 'vue'
import { store } from '@/store'
import { sendInvite, getMyFriends, shareWithFriends } from '@/api-service'
import { giftShareMode } from '@/utils/care-package'
import { parseRecipients, validRecipients } from '@/utils/recipients'
import { trackShare } from '@/utils/analytics'
import RatingCrystals from '@/components/ui/RatingCrystals.vue'
import GiftSend from '@/components/ui/share/GiftSend.vue'

const props = defineProps({
  url: { type: String, default: '' },       // public /s/<slug> link
  shared: { type: Boolean, default: false }, // is the reading currently shared
  // Resonance gate: set when publishing is blocked until the seeker rates 3+ globes.
  gate: { type: Object, default: null },     // { message, rating, min }
  // The toggle request is out: the pill shows it and won't fire again.
  busy: { type: Boolean, default: false },
  // Care Package: when the reading was made for someone, this is the whole
  // screen: one recipient, never public. {is_gift, for_name, for_querent,
  // signed_as, from_name, gift_note, reading_session_id}
  gift: { type: Object, default: null },
  sessionId: { type: String, default: '' },
  // The reading's picture: the top of the gift letter.
  image: { type: String, default: '' },
  // Texting is wired and off until Chris turns it on (care package payload
  // `sms_enabled`). The invite is email only either way (texting plan,
  // 2026-10-31): a typed number is turned away in place. Kept so the page's
  // binding stays valid; nothing here texts.
  smsEnabled: { type: Boolean, default: false },
  // Stage 4 (recipient journey R7.9): her reading for someone is rated before
  // it goes; the rating sits above the contact field.
})
const emit = defineEmits(['close', 'toggle-public', 'rated', 'sent', 'linked', 'changed', 'need-carats'])

// ── A gift: its own screen (GiftSend.vue, 2026-10-21, gift send flow FINAL) ──
const giftMode = computed(() => giftShareMode(props.gift))

// Pill toggle: only fire the flip when moving to the OTHER state.
function setPublic(val) { if (props.busy) return; if (val !== props.shared) emit('toggle-public') }

const copied = ref(false)
const inviteText = ref('')
const inviteStatus = ref('')   // '', 'sending', 'sent', 'error'
const friends = ref([])
const selectedFriends = ref([])
const friendQuery = ref('')
const pickerOpen = ref(false)

const initial = (f) => (f.nick_name || '?').charAt(0)
const selectedFriendObjs = computed(() =>
  friends.value.filter((f) => selectedFriends.value.includes(f.querent_uuid)))
const filteredFriends = computed(() => {
  const q = friendQuery.value.trim().toLowerCase()
  return friends.value.filter((f) =>
    !selectedFriends.value.includes(f.querent_uuid)
    && (!q || (f.nick_name || '').toLowerCase().includes(q)))
})
function addFriend(id) { if (!selectedFriends.value.includes(id)) selectedFriends.value.push(id); friendQuery.value = '' }
function removeFriend(id) { selectedFriends.value = selectedFriends.value.filter((x) => x !== id) }
function closePickerSoon() { setTimeout(() => { pickerOpen.value = false }, 150) }

const friendShareStatus = ref('')   // '', 'sending', 'sent'
async function shareFriends() {
  if (!selectedFriends.value.length || friendShareStatus.value === 'sending') return
  friendShareStatus.value = 'sending'
  try {
    await shareWithFriends(store.latest_reading_session, selectedFriends.value, store.session_id)
    trackShare('friends', 'reading', store.latest_reading_session, { recipients: selectedFriends.value.length })
    friendShareStatus.value = 'sent'
    selectedFriends.value = []
    setTimeout(() => { friendShareStatus.value = '' }, 3000)
  } catch (e) { friendShareStatus.value = '' }
}

const parsed = computed(() => parseRecipients(inviteText.value))
// Email only: a number never goes from here.
const sendable = computed(() => validRecipients(inviteText.value).filter((r) => r.type !== 'phone'))
const validCount = computed(() => sendable.value.length)
const invalidCount = computed(() => parsed.value.filter((r) => r.type === 'invalid').length)
const SMS_OFF = computed(() => (props.shared
  ? 'We send invites by email only. Add their email, or copy the link above and text it yourself.'
  : 'We send invites by email only. Add their email instead.'))
const smsTurnedAway = computed(() => parsed.value.some((r) => r.type === 'phone'))
const inviteError = ref('')      // the server's words, else the plain retry line
const notSent = ref([])          // [{ shown, reason }] the server came back ok:false for
const sentToSome = ref(false)    // some went beside the ones that did not
function notSentLine(n) {
  return n.reason === 'opted_out'
    ? `Not sent to ${n.shown}: that address asked not to get mail from us.`
    : `Not sent to ${n.shown}. Check it and try again.`
}

onMounted(async () => {
  // A gift opens GiftSend; the ordinary picker never mounts.
  if (giftMode.value) return
  try {
    const data = await getMyFriends(store.session_id)
    friends.value = (data && data.friends) || []
  } catch (e) { /* picker simply stays hidden */ }
})

async function copyLink() {
  const done = () => {
    copied.value = true; setTimeout(() => { copied.value = false }, 2000)
    trackShare('copy_link', 'reading', store.latest_reading_session)
  }
  try { await navigator.clipboard.writeText(props.url); done() }
  catch (e) {
    const ta = document.createElement('textarea')
    ta.value = props.url; ta.style.position = 'fixed'; ta.style.opacity = '0'
    document.body.appendChild(ta); ta.select()
    try { document.execCommand('copy'); done() } catch (_) { /* no-op */ }
    document.body.removeChild(ta)
  }
}

async function send() {
  const recipients = sendable.value
  if (!recipients.length || inviteStatus.value === 'sending') return
  inviteStatus.value = 'sending'; inviteError.value = ''; notSent.value = []; sentToSome.value = false
  try {
    const r = await sendInvite(store.latest_reading_session, recipients, store.current_reader_name || 'A seeker', store.session_id)
    // The server answers per recipient; ok:false (an opted-out number, a
    // failed delivery) did not go, and is never called sent.
    const failed = ((r && r.sent) || []).filter((x) => x && x.ok === false)
    const isFailed = (rc) => failed.some((x) => x.value === rc.value)
    const went = recipients.filter((rc) => !isFailed(rc))
    // Only what went (email only).
    if (went.length) trackShare('email', 'reading', store.latest_reading_session, { recipients: went.length })
    notSent.value = recipients.filter(isFailed).map((rc) => ({
      shown: (parsed.value.find((x) => x.value === rc.value && x.raw) || {}).raw || rc.value, reason: (failed.find((x) => x.value === rc.value) || {}).reason || '',
    }))
    sentToSome.value = notSent.value.length > 0 && went.length > 0
    // What did not go stays in the box to fix; what went leaves it.
    inviteText.value = notSent.value.map((n) => n.shown).join(', ')
    inviteStatus.value = went.length ? 'sent' : ''
    if (went.length) setTimeout(() => { if (inviteStatus.value === 'sent') inviteStatus.value = '' }, 3000)
  } catch (e) {
    const said = e && e.response && e.response.data && e.response.data.error
    inviteError.value = said || 'That did not go through. Try again.'
    inviteStatus.value = 'error'
  }
}
</script>

<template>
  <!-- A gift: the letter and one button (its own screen). -->
  <GiftSend v-if="giftMode" :gift="gift" :session-id="sessionId" :image="image"
            @close="emit('close')" @sent="emit('sent', $event)" @linked="emit('linked', $event)" @changed="emit('changed', $event)"
            @need-carats="emit('need-carats')" />
  <Teleport v-else to="body">
    <div class="sp-overlay" @click.self="emit('close')">
      <div class="sp" role="dialog" aria-modal="true" aria-label="Share this reading">
        <button class="sp-close" aria-label="Close" @click="emit('close')"><i class="fas fa-times" aria-hidden="true"></i></button>

        <!-- Private/Public state as a segmented pill toggle. -->
        <div class="sp-hero">
          <div class="sp-pillbar" role="tablist" aria-label="Reading visibility">
            <button
              class="sp-pill" :class="{ active: !shared }" role="tab" :aria-selected="!shared"
              @click="setPublic(false)"
            ><i class="fas fa-lock"></i> Private</button>
            <button
              class="sp-pill" :class="{ active: shared, busy }" role="tab" :aria-selected="shared"
              :aria-busy="busy ? 'true' : null" :disabled="busy"
              @click="setPublic(true)"
            ><i v-if="busy" class="fas fa-circle-notch fa-spin sp-spin"></i><i v-else class="fas fa-globe"></i> {{ busy ? 'Making public…' : 'Public' }}</button>
          </div>
          <p class="sp-hero-sub">
            {{ shared
              ? 'Public - visible in the Community & on Mystic pages, and to anyone with the link.'
              : 'Only you can see it. Invite friends below, or make it public to surface it in the Community & on Mystic pages.' }}
          </p>

          <!-- Resonance gate: a reading joins the Circle only if it resonated.
               Rate it 3+ globes right here and the publish retries automatically. -->
          <div v-if="gate" class="sp-gate">
            <p class="sp-gate-msg">{{ gate.message }}</p>
            <RatingCrystals @rated="emit('rated', $event)" />
          </div>
        </div>

        <!-- Copy link — only meaningful once public (the /s/ page is live then). -->
        <div v-if="shared" class="sp-linkrow">
          <input class="sp-url" :value="url" readonly @focus="$event.target.select()" />
          <button class="sp-copy" :class="{ copied }" @click="copyLink">
            <i :class="copied ? 'fas fa-check' : 'fas fa-link'"></i><span>{{ copied ? 'Copied!' : 'Copy Link' }}</span>
          </button>
        </div>

        <!-- Friend picker (only when the seeker has friends) — works in both
             states; this is how private readings reach your circle. -->
        <!-- Friends picker — PRIVATE only. Public readings reach friends' feeds
             automatically, so there's nothing to pick there. Searchable
             multi-select so it scales past a few friends. -->
        <div v-if="!shared && friends.length" class="sp-friends">
          <div class="sp-section-label">Share with friends</div>
          <div class="sp-picker" :class="{ open: pickerOpen }">
            <span v-for="f in selectedFriendObjs" :key="f.querent_uuid" class="sp-chip">
              <span class="sp-friend-avatar">{{ initial(f) }}</span>{{ f.nick_name }}
              <button type="button" class="sp-chip-x" @click="removeFriend(f.querent_uuid)">×</button>
            </span>
            <input
              class="sp-picker-input" v-model="friendQuery"
              :placeholder="selectedFriends.length ? 'Add another…' : 'Search friends…'"
              @focus="pickerOpen = true" @blur="closePickerSoon"
            />
          </div>
          <ul v-if="pickerOpen && filteredFriends.length" class="sp-picker-list">
            <li v-for="f in filteredFriends" :key="f.querent_uuid" @mousedown.prevent="addFriend(f.querent_uuid)">
              <span class="sp-friend-avatar">{{ initial(f) }}</span><span>{{ f.nick_name }}</span>
            </li>
          </ul>
          <p v-else-if="pickerOpen && friendQuery" class="sp-picker-empty">No friends match “{{ friendQuery }}”.</p>
          <button
            v-if="selectedFriends.length" class="sp-share-friends"
            :disabled="friendShareStatus === 'sending'" @click="shareFriends"
          >
            {{ friendShareStatus === 'sending'
              ? 'Sharing…'
              : `Share with ${selectedFriends.length} friend${selectedFriends.length > 1 ? 's' : ''}` }}
          </button>
          <p v-if="friendShareStatus === 'sent'" class="sp-ok">✦ Sent to their messages.</p>
        </div>

        <!-- Invite by email (only) -->
        <div class="sp-invite">
          <div class="sp-section-label">Invite by email</div>
          <textarea
            v-model="inviteText" rows="2" class="sp-invite-input"
            placeholder="Emails, separated by commas"
          ></textarea>
          <p v-if="smsTurnedAway" class="sp-note" data-testid="invite-sms-off">{{ SMS_OFF }}</p>
          <div class="sp-invite-row">
            <span class="sp-invite-meta">
              <template v-if="validCount">{{ validCount }} recipient{{ validCount > 1 ? 's' : '' }}</template>
              <span v-if="invalidCount" class="sp-invalid"> · {{ invalidCount }} invalid</span>
            </span>
            <button class="sp-send" :disabled="!validCount || inviteStatus === 'sending'" @click="send">
              {{ inviteStatus === 'sending' ? 'Sending…' : 'Send invite' }}
            </button>
          </div>
          <p v-if="inviteStatus === 'sent'" class="sp-ok">{{ sentToSome ? '✦ Sent to the others.' : '✦ Invite sent.' }}</p>
          <p v-else-if="inviteStatus === 'error'" class="sp-err">{{ inviteError }}</p>
          <div v-if="notSent.length" class="sp-err" data-testid="invite-not-sent">
            <p v-for="n in notSent" :key="n.shown">{{ notSentLine(n) }}</p>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.sp-overlay { position: fixed; inset: 0; z-index: 1200; display: flex; align-items: center; justify-content: center; padding: 20px; padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px)); background: rgba(20, 8, 40, 0.55); }
.sp { position: relative; box-sizing: border-box; width: 100%; max-width: 440px; background: #fff; border-radius: 18px; padding: 24px; box-shadow: 0 24px 60px -12px rgba(20, 8, 40, 0.5); border: 1px solid #ece3f7; max-height: 88dvh; overflow-y: auto; }
.sp-close { position: absolute; top: 8px; right: 8px; display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 50%; background: #f3e8ff; color: #4a236b; border: none; cursor: pointer; z-index: 2; font-size: 15px; line-height: 1; }

/* Hero — pill toggle + one descriptive line. */
.sp-hero { text-align: center; padding: 6px 8px 20px; margin: 6px 0 16px; border-bottom: 1px solid #f0e8fa; }
/* Segmented Private/Public pill toggle. */
.sp-pillbar { display: inline-flex; padding: 4px; gap: 4px; border-radius: 999px; background: #f3e8ff; border: 1px solid #e3d4f5; }
.sp-pill.busy { cursor: progress; opacity: 0.85; }
.sp-spin { animation: sp-spin 0.9s linear infinite; }
@keyframes sp-spin { to { transform: rotate(360deg); } }
.sp-pill { display: inline-flex; align-items: center; gap: 7px; border: none; background: transparent; color: #6b5a82; font-size: 13.5px; font-weight: 700; padding: 8px 20px; border-radius: 999px; cursor: pointer; transition: all 0.15s; }
.sp-pill i { font-size: 12px; }
.sp-pill.active { color: #fff; background: linear-gradient(180deg, #7b4bb0, #4e2776); box-shadow: 0 6px 16px -8px rgba(78, 39, 118, 0.8); }
.sp-hero-sub { margin: 14px auto 0; max-width: 360px; font-size: 13px; line-height: 1.5; color: #6b5a82; }
.sp-gate { margin: 14px auto 0; max-width: 380px; padding: 12px; border-radius: 12px; background: #fbf4ff; border: 1px solid #ead9f7; }
.sp-gate-msg { margin: 0; font-size: 12.5px; line-height: 1.5; color: #6b3fa0; font-weight: 600; }
.sp-linkrow { display: flex; gap: 8px; margin-bottom: 4px; }
.sp-url { flex: 1; min-width: 0; padding: 11px 13px; font-size: 16px; color: #4a3a62; background: #faf7ff; border: 1px solid #e3d4f5; border-radius: 10px; outline: none; }
.sp-copy { flex-shrink: 0; display: inline-flex; align-items: center; gap: 8px; padding: 11px 16px; border: none; border-radius: 10px; cursor: pointer; background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; font-size: 14px; font-weight: 700; white-space: nowrap; }
.sp-copy.copied { background: #2f8f2f; }
.sp-section-label { font-family: 'Cinzel', serif; font-size: 12px; font-weight: 600; color: #4a236b; margin: 14px 0 6px; letter-spacing: 0.04em; }
.sp-friends { border-top: 1px dashed #ece3f7; margin-top: 16px; padding-top: 6px; position: relative; }
/* Searchable multi-select: chips + a filter input in one box. */
.sp-picker { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; padding: 6px 8px; border: 1px solid #e3d4f5; border-radius: 10px; background: #faf7ff; }
.sp-picker.open { border-color: #c9a9ff; box-shadow: 0 0 0 3px rgba(123, 75, 176, 0.12); }
.sp-picker-input { flex: 1; min-width: 120px; border: none; background: transparent; outline: none; font-size: 16px; color: #4a3a62; padding: 4px; }
.sp-chip { display: inline-flex; align-items: center; gap: 6px; padding: 3px 6px 3px 3px; font-size: 12.5px; font-weight: 600; color: #fff; background: linear-gradient(180deg, #7b4bb0, #4e2776); border-radius: 999px; }
.sp-chip-x { border: none; background: transparent; color: rgba(255,255,255,0.85); font-size: 15px; line-height: 1; cursor: pointer; padding: 0 2px; }
.sp-friend-avatar { display: flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: #6b3fa0; color: #fff; font-size: 11px; font-weight: 700; text-transform: uppercase; flex-shrink: 0; }
.sp-chip .sp-friend-avatar { background: rgba(255,255,255,0.25); }
/* Dropdown of matches. */
.sp-picker-list { list-style: none; margin: 4px 0 0; padding: 4px; max-height: 168px; overflow-y: auto; border: 1px solid #e3d4f5; border-radius: 10px; background: #fff; box-shadow: 0 12px 28px -10px rgba(20, 8, 40, 0.35); }
.sp-picker-list li { display: flex; align-items: center; gap: 8px; padding: 7px 8px; border-radius: 8px; font-size: 13px; color: #4a3a62; cursor: pointer; }
.sp-picker-list li:hover { background: #f3e8ff; }
.sp-picker-empty { margin: 6px 2px 0; font-size: 12px; color: #9b8bb0; }
.sp-share-friends { margin-top: 10px; width: 100%; padding: 11px; border: none; border-radius: 999px; font-size: 13.5px; font-weight: 700; color: #fff; background: linear-gradient(180deg, #7b4bb0, #4e2776); cursor: pointer; box-shadow: 0 8px 20px -10px rgba(78,39,118,0.9); }
.sp-share-friends:disabled { opacity: 0.6; cursor: default; }
.sp-invite { border-top: 1px dashed #ece3f7; margin-top: 16px; padding-top: 6px; }
.sp-invite-input { width: 100%; box-sizing: border-box; padding: 10px 12px; font-size: 16px; border: 1px solid #e3d4f5; border-radius: 10px; resize: vertical; font-family: inherit; }
.sp-invite-row { display: flex; align-items: center; justify-content: space-between; margin-top: 8px; }
.sp-invite-meta { font-size: 12px; color: #6b5a82; }
.sp-send { background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; border: none; border-radius: 999px; padding: 9px 18px; font-size: 13px; font-weight: 700; cursor: pointer; }
.sp-send:disabled { opacity: 0.5; cursor: default; }
.sp-ok { margin: 8px 0 0; font-size: 12.5px; color: #2f8f2f; font-weight: 600; }
.sp-err { margin: 8px 0 0; font-size: 12.5px; color: #b4622a; font-weight: 600; }
.sp-err p { margin: 0; }
.sp-note { margin: 6px 0 0; font-size: 12.5px; color: #6b5a82; }

</style>
